export type NetworkingDetail = {
  name: string;
  phone: string;
  email: string;
  businessName: string;
  businessCategory: string;
  subCategory: string;
  notes: string;
};

export type BusinessCategoryOption = {
  category: string;
  subCategory: string;
};

export function emptyNetworkingDetail(): NetworkingDetail {
  return {
    name: '',
    phone: '',
    email: '',
    businessName: '',
    businessCategory: '',
    subCategory: '',
    notes: '',
  };
}

function parseNetworkingDetail(value: unknown): NetworkingDetail {
  const empty = emptyNetworkingDetail();
  if (value == null || typeof value !== 'object') return empty;
  const row = value as Record<string, unknown>;
  return {
    name: typeof row.name === 'string' ? row.name : '',
    phone: typeof row.phone === 'string' ? row.phone : '',
    email: typeof row.email === 'string' ? row.email : '',
    businessName: typeof row.businessName === 'string' ? row.businessName : '',
    businessCategory: typeof row.businessCategory === 'string' ? row.businessCategory : '',
    subCategory: typeof row.subCategory === 'string' ? row.subCategory : '',
    notes: typeof row.notes === 'string' ? row.notes : '',
  };
}

export function parseDreamReferralList(value: unknown): NetworkingDetail[] {
  if (!Array.isArray(value)) return [];
  return value.map(parseNetworkingDetail).filter(hasAnyNetworkingDetail);
}

export function hasAnyNetworkingDetail(value: NetworkingDetail) {
  return Boolean(
    value.name.trim() ||
      value.phone.trim() ||
      value.email.trim() ||
      value.businessName.trim() ||
      value.notes.trim() ||
      (value.businessCategory.trim() && value.subCategory.trim())
  );
}

export function formatNetworkingDetail(value: NetworkingDetail | null | undefined) {
  if (!value) return '';
  const parts = [
    value.name,
    value.phone,
    value.email,
    value.businessName,
    value.businessCategory && value.subCategory
      ? `${value.businessCategory} > ${value.subCategory}`
      : value.businessCategory || value.subCategory,
    value.notes,
  ].filter((part) => !!part?.trim());
  return parts.join(' · ');
}

export function parseCategoryOptions(value: unknown): BusinessCategoryOption[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const row = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      return {
        category: typeof row.category === 'string' ? row.category.trim() : '',
        subCategory: typeof row.subCategory === 'string' ? row.subCategory.trim() : '',
      };
    })
    .filter((option) => option.category || option.subCategory)
    .slice(0, 20);
}

export function ensureDetailEntries(list: NetworkingDetail[] | null | undefined): NetworkingDetail[] {
  if (!list?.length) return [emptyNetworkingDetail()];
  return list.map((item) => ({ ...emptyNetworkingDetail(), ...item }));
}

export function categoryLabel(option: BusinessCategoryOption) {
  return option.category && option.subCategory
    ? `${option.category} > ${option.subCategory}`
    : option.subCategory || option.category;
}
